/**
 * Custom fetch client for Orval-generated API
 * Handles authentication and dev mode headers
 */
import { config } from '../utils/config';
import { storage, STORAGE_KEYS } from '../utils/storage';

/**
 * Custom fetch function with authentication
 * Used by Orval-generated API client
 */
export const customFetch = async <T>(url: string, options?: RequestInit): Promise<T> => {
  // Get token from storage
  const token = storage.get<string>(STORAGE_KEYS.AUTH_TOKEN);

  // Build headers. Fastify rejects requests carrying Content-Type: application/json with an
  // empty body (400 FST_ERR_CTP_EMPTY_JSON_BODY), so the header is only set when a body exists.
  const headers: Record<string, string> = {
    ...(options?.body != null ? { 'Content-Type': 'application/json' } : {}),
    ...(options?.headers as Record<string, string>),
  };

  // Add auth token if available
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Add dev user ID header if in dev mode
  if (!import.meta.env.PROD && config.devAuth && config.devUserId) {
    headers['X-Dev-User-Id'] = config.devUserId.toString();
  }

  // Make full URL with base URL
  const fullUrl = url.startsWith('http') ? url : `${config.apiBaseUrl}${url}`;

  // Retry logic with exponential backoff (3 attempts for network/server errors)
  const MAX_RETRIES = 2;
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(fullUrl, {
        ...options,
        headers,
      });

      // Handle auth errors (no retry)
      if (response.status === 401 || response.status === 403) {
        storage.remove(STORAGE_KEYS.AUTH_TOKEN);
        throw new Error('Unauthorized');
      }

      // Parse response
      const contentType = response.headers.get('content-type');
      let data: unknown;

      if (contentType?.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      // Server errors are retryable
      if (response.status >= 500 && attempt < MAX_RETRIES) {
        lastError = new Error(`Server error: ${response.status}`);
        await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt)));
        continue;
      }

      // Check for errors
      if (!response.ok) {
        throw new Error(`Request failed: ${response.statusText}`);
      }

      // Return Orval-compatible response object
      return {
        data,
        status: response.status,
        headers: response.headers,
      } as T;
    } catch (error) {
      // Auth errors should not be retried
      if (error instanceof Error && error.message === 'Unauthorized') {
        throw error;
      }

      lastError = error instanceof Error ? error : new Error(String(error));

      // Network errors (TypeError: Failed to fetch) are retryable
      if (attempt < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt)));
        continue;
      }
    }
  }

  throw lastError || new Error('Request failed after retries');
};
